import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";

export interface Dividend {
  symbol: string;
  date: string;
  recordDate: string;
  paymentDate: string;
  declarationDate: string;
  adjDividend: number;
  dividend: number;
  yield: number;
  frequency: 'Annual' | 'Quarterly' | 'Monthly' | 'Semi-Annual' | 'Special' | 'Other';
}

// Helper to check if error is a 404
export const isDividendNotFoundError = (error: Error | null): boolean => {
  if (!error) return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (error as any)?.response?.status === 404;
};

export function useCompanyDividends(ticker: string, limit?: number) {
  return useQuery<Dividend[], Error>({
    queryKey: ["companyDividends", ticker, limit],
    queryFn: async () => {
      const params: Record<string, string> = { ticker };
      if (limit) {
        params.limit = limit.toString();
      }

      const { data } = await api.get<Dividend[]>("/company-dividends", {
        params,
      });
      return data;
    },
    enabled: !!ticker,
    refetchOnWindowFocus: false,
    staleTime: 2.5 * 60 * 1000, // 2.5 minutes
    gcTime: 8 * 60 * 60 * 1000, // 8 hours cache time
    retry: (failureCount, error) => {
      // Don't retry on 404s - legitimately means no dividend data exists (growth stocks, etc.)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any)?.response?.status === 404) {
        return false;
      }
      // Retry other errors (500s, network issues) max 2 times
      return failureCount < 2;
    },
  });
}
